import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { prepareScheduleForScreen } from '../../ServiceTestsScheduleTransforms';
import { SHOULD_DIRTY_FALSE } from '../../ServiceTestsFormControlConstants';
import { MULTI } from '../../../general/Constants';
import { ADAPTER_TYPE_FIELD_NAME, SCHEDULING_FIELD_NAME } from '../../ServiceTestsConstants';

/**
 * Bridges the parent form's nested `scheduling` API object with the flat fields
 * that ScheduleFields reads/writes (scheduleType, interval, isWindow, windowStart,
 * windowEnd, dailyStart).
 *
 * Watches `scheduling` in the parent react-hook-form context. When it changes
 * (e.g., after useFindById resets the form with API data), transforms it via
 * prepareScheduleForScreen and writes each flat field back with shouldDirty:false
 * so the form is not marked dirty during initial population.
 *
 * Also watches `adapterType` so that flat fields are re-populated after switching
 * back from 'multi'. Under shouldUnregister:true, ScheduleFields unmounts when
 * adapterType is 'multi', which unregisters the flat fields. When the user switches
 * back, ScheduleFields re-mounts but the effect would not re-fire without
 * adapterType in the dependency list (scheduling itself has not changed).
 *
 * @param {{ intervals: Array<{ value: number, text: string }> }} params
 *   `intervals` must be referentially stable across renders (e.g., wrapped in useMemo
 *   by the caller). Passing a new array on every render will cause unnecessary
 *   re-execution of the scheduling transform.
 */
const useScheduleFormBridge = ({ intervals }) => {
    const { setValue } = useFormContext();
    const scheduling = useWatch({ name: SCHEDULING_FIELD_NAME });
    const adapterType = useWatch({ name: ADAPTER_TYPE_FIELD_NAME });

    useEffect(() => {
        // Wait for real API data — undefined means useFindById has not yet resolved.
        if (scheduling === undefined) return;

        // Skip when multi — ScheduleFields is not rendered and flat fields
        // are about to be unregistered anyway.
        if (adapterType === MULTI) return;

        const flat = prepareScheduleForScreen(scheduling, intervals);
        Object.entries(flat).forEach(([key, value]) => {
            setValue(key, value, SHOULD_DIRTY_FALSE);
        });
    }, [scheduling, intervals, setValue, adapterType]);
};

export default useScheduleFormBridge;
